// field-visibility.config.ts
/**
 * ============================================================
 * Field Visibility Config (field-visibility.config.ts)
 * ============================================================
 *
 * WHY THIS FILE IS NEEDED:
 * - In the Schedule Details screen, almost every field is shown or hidden
 *   based on conditions (session type, mandate status, etc.).
 * - Instead of writing those conditions everywhere in the template,
 *   we keep them in one place (this config file).
 * - This makes the logic easy to maintain and ensures consistency
 *   across the whole ATLAS project (since scheduling is used in many areas).
 *
 * HOW IT WORKS:
 * - `FIELD_VISIBILITY` is an object where each key is a field name
 *   and the value is a function that returns true/false.
 * - The function checks the current state of the form/component
 *   (like session type, mandate status, etc.).
 * - In the template, we use:
 *     *ngIf="fieldVisibilityConfig.students(this)"
 *   to decide if the field should be visible.
 *
 * DEVELOPER NOTES:
 * - When adding a new field, define its condition here instead of
 *   writing inline checks in the component/template.
 * - Always use enums/constants (SessionType, MANDATE_STATUS, etc.)
 *   instead of hardcoding IDs.
 * - This keeps all visibility rules in one place and makes future
 *   changes safer and faster.
 *
 * ============================================================
 */

import { ScheduleDetailsComponent } from 'src/app/shared/components/schedule-details/schedule-details.component';
import { MANDATE_STATUS } from 'src/app/modules/provider-management/constants/mandates-status.constants';
import {
  Occurrences,
  RepeatUntil,
  SessionType,
} from 'src/app/modules/provider-management/enums/schedule-enums';

export type ScheduleFieldVisibilityConfig = Record<
  string,
  (comp: ScheduleDetailsComponent) => boolean
>;

function getSessionTypeId(comp: ScheduleDetailsComponent): number | null {
  const value = comp?.scheduleDetailsForm?.get('sessionType')?.value;
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'object' ? value?.id : value;
}

function isIndirectEncounterSession(sessionTypeId: number | null): boolean {
  return [
    SessionType.IEPMeetingAttendance,
    SessionType.NYCPSAuthorizedTraining,
    SessionType.MeetingWithSchoolStaffAndFamily,
  ].includes(sessionTypeId);
}

function showRelatedServiceFields(comp: ScheduleDetailsComponent): boolean {
  const sessionTypeId = getSessionTypeId(comp);

  if (sessionTypeId !== SessionType.RelatedServicesAppointment) {
    return false;
  }

  return [
    MANDATE_STATUS.AwaitingServiceConfirmation,
    MANDATE_STATUS.Receiving,
    MANDATE_STATUS.AllMandates,
  ].includes(comp.providerManagementInfo?.statusID);
}

export const SCHEDULE_FIELD_VISIBILITY: ScheduleFieldVisibilityConfig = {
  sessionType: () => true,
  makeupSession: (comp: ScheduleDetailsComponent): boolean => {
    const isRelatedServiceAppoint: boolean =
      getSessionTypeId(comp) === SessionType.RelatedServicesAppointment;
    const isAwaitingServiceConfirmation: boolean =
      comp.providerManagementInfo?.statusID ===
      MANDATE_STATUS.AwaitingServiceConfirmation;
    const receivingConfirmSection: boolean =
      comp.providerManagementInfo?.statusID ===
      MANDATE_STATUS.Receiving;
    const isAllMandates: boolean =
      comp.providerManagementInfo?.statusID ===
      MANDATE_STATUS.AllMandates;
    if (isRelatedServiceAppoint && (isAwaitingServiceConfirmation || receivingConfirmSection || isAllMandates)) {
      return true;
    }
    return false;
  },
  startDate: () => true, ////always visible
  startTime: () => true, ////always visible
  endTime: () => true, ////always visible
  absencePeriod: (comp: ScheduleDetailsComponent): boolean => {
    const isProviderAbsence: boolean =
      getSessionTypeId(comp) === SessionType.ProviderAbsence;
    if (isProviderAbsence) {
      return true;
    }
    return false;
  },

  occurencesSection: (comp: ScheduleDetailsComponent): boolean => {
    const sessionTypeId = getSessionTypeId(comp);
    const isRelatedServiceAppoint: boolean =
      sessionTypeId === SessionType.RelatedServicesAppointment;
    const isAwaitingServiceConfirmation: boolean =
      comp.providerManagementInfo?.statusID ===
      MANDATE_STATUS.AwaitingServiceConfirmation;
    const isAllMandates: boolean =
      comp.providerManagementInfo?.statusID ===
      MANDATE_STATUS.AllMandates 
    const isPreparationTime: boolean =
      sessionTypeId === SessionType.PreparationTime;
    const recordTimeforSessionNotes: boolean =
      sessionTypeId === SessionType.TimeSpentEnteringSessionNotes;
    const isProgressReporting: boolean =
      sessionTypeId === SessionType.TimeSpentProgressReporting;
    const isProviderAbsence: boolean =
      sessionTypeId === SessionType.ProviderAbsence;
    const receivingConfirmSection: boolean =
      comp.providerManagementInfo?.statusID ===
      MANDATE_STATUS.Receiving;
    if (comp.isNew === false && comp.isEditingSession === false) {
      return true;
    } else if (
      comp.isNew &&
      isRelatedServiceAppoint && (isAwaitingServiceConfirmation || receivingConfirmSection || isAllMandates)
    ) {
      return true;
    } else if (
      isPreparationTime ||
      recordTimeforSessionNotes ||
      isProgressReporting ||
      isProviderAbsence
    ) {
      return true;
    }
    return false;
  },
  occurences: () => true, ////always visible
  mandates: (comp: ScheduleDetailsComponent): boolean => {
    const isAwaitingServiceConfirmation: boolean =
      comp.providerManagementInfo?.statusID ===
      MANDATE_STATUS.AwaitingServiceConfirmation;
    const isAllMandates: boolean =
      comp.providerManagementInfo?.statusID ===
      MANDATE_STATUS.AllMandates;
    const isRelatedServiceAppoint: boolean =
      getSessionTypeId(comp) === SessionType.RelatedServicesAppointment;
    const receivingConfirmSection: boolean =
      comp.providerManagementInfo?.statusID ===
      MANDATE_STATUS.Receiving;
    if (isRelatedServiceAppoint && (isAwaitingServiceConfirmation || receivingConfirmSection || isAllMandates)) {
      return true;
    }
    return false;
  },
  students: (comp: ScheduleDetailsComponent): boolean => {
    const isIEPMeetingAttendence: boolean =
      getSessionTypeId(comp) === SessionType.IEPMeetingAttendance;
    if (isIEPMeetingAttendence) {
      return false;
    }
    return false;
  },
  studentsIepMeeting: (comp: ScheduleDetailsComponent): boolean => {
    const isIEPMeetingAttendence: boolean =
      getSessionTypeId(comp) === SessionType.IEPMeetingAttendance;
    if (isIEPMeetingAttendence) {
      return true;
    }
    return false;
  },
  requiredAttendees: (comp: ScheduleDetailsComponent): boolean => {
    const sessionTypeId = getSessionTypeId(comp);
    return false;
  },
  optionalAttendees: (comp: ScheduleDetailsComponent): boolean => {
    const sessionTypeId = getSessionTypeId(comp);
    return false;
  },
  teamsMeeting: (comp: ScheduleDetailsComponent): boolean => {
    const sessionTypeId = getSessionTypeId(comp);
    if (isIndirectEncounterSession(sessionTypeId)) {
      return false; 
    }
    return showRelatedServiceFields(comp);
  },
  additionalInformation: (comp: ScheduleDetailsComponent): boolean => {
    const sessionTypeId = getSessionTypeId(comp);
    const isPreparationTime: boolean =
      sessionTypeId === SessionType.PreparationTime;
    const isIEPMeetingAttendence: boolean =
      sessionTypeId === SessionType.IEPMeetingAttendance;
    const isRecordSessionNotes: boolean =
      sessionTypeId === SessionType.TimeSpentEnteringSessionNotes;
    const isTimeSpentProgressReporting: boolean =
      sessionTypeId === SessionType.TimeSpentProgressReporting;
    const isNYCPSAuthorizedTraining: boolean =
      sessionTypeId === SessionType.NYCPSAuthorizedTraining;
    const isMeetStaffAndFamMember: boolean =
      sessionTypeId === SessionType.MeetingWithSchoolStaffAndFamily;
    const isProviderAbsence: boolean =
      sessionTypeId === SessionType.ProviderAbsence;
    const isRelatedServiceAppoint: boolean =
      sessionTypeId === SessionType.RelatedServicesAppointment;
    const isAwaitingServiceConfirmation: boolean =
      comp.providerManagementInfo?.statusID ===
      MANDATE_STATUS.AwaitingServiceConfirmation;
    const isAllMandates: boolean =
      comp.providerManagementInfo?.statusID ===
      MANDATE_STATUS.AllMandates;
    const receivingConfirmSection: boolean =
      comp.providerManagementInfo?.statusID ===
      MANDATE_STATUS.Receiving;
    if (isRelatedServiceAppoint && (isAwaitingServiceConfirmation || receivingConfirmSection || isAllMandates)) {
      return true;
    } else if (
      isPreparationTime ||
      isIEPMeetingAttendence ||
      isRecordSessionNotes ||
      isTimeSpentProgressReporting ||
      isNYCPSAuthorizedTraining ||
      isMeetStaffAndFamMember ||
      isProviderAbsence
    ) {
      return true;
    }
    if (
      isPreparationTime ||
      isIEPMeetingAttendence ||
      isRecordSessionNotes ||
      isNYCPSAuthorizedTraining ||
      isMeetStaffAndFamMember ||
      isProviderAbsence
    ) {
      return true;
    }
    return false;
  },
  locationSection: (comp: ScheduleDetailsComponent): boolean => {
    const sessionTypeId = getSessionTypeId(comp);
    if (isIndirectEncounterSession(sessionTypeId)) {
    return false;
  }
  return showRelatedServiceFields(comp);
  },
  minutes: (comp: ScheduleDetailsComponent): boolean => {
    const isAwaitingServiceConfirmation: boolean =
      comp.providerManagementInfo?.statusID ===
      MANDATE_STATUS.AwaitingServiceConfirmation;
    const isAllMandates: boolean =
      comp.providerManagementInfo?.statusID ===
      MANDATE_STATUS.AllMandates;
    const isRelatedServiceAppoint: boolean =
      getSessionTypeId(comp) === SessionType.RelatedServicesAppointment;
    if ((isAwaitingServiceConfirmation || isAllMandates) && isRelatedServiceAppoint) {
      return true;
    }
    return false;
  },
  sessionDetails: (comp: ScheduleDetailsComponent): boolean => {
    const isAwaitingServiceConfirmation: boolean =
      comp.providerManagementInfo?.statusID ===
      MANDATE_STATUS.AwaitingServiceConfirmation;
    const isAllMandates: boolean =
      comp.providerManagementInfo?.statusID ===
      MANDATE_STATUS.AllMandates;
    const isRelatedServiceAppoint: boolean =
      getSessionTypeId(comp) === SessionType.RelatedServicesAppointment;
    const receivingConfirmSection: boolean =
      comp.providerManagementInfo?.statusID ===
      MANDATE_STATUS.Receiving;
    if (isRelatedServiceAppoint && (isAwaitingServiceConfirmation || receivingConfirmSection || isAllMandates)) {
      return true;
    }
    return false;
  },
  trainingDescription: (comp: ScheduleDetailsComponent): boolean => {
    const isNYCPSAuthorizedTraining: boolean =
      getSessionTypeId(comp) === SessionType.NYCPSAuthorizedTraining;
    if (isNYCPSAuthorizedTraining) {
      return true;
    }
    return false;
  },

  dateTimeSection: () => true,

  everyText: (comp: ScheduleDetailsComponent) => {
    const id: number = comp.scheduleDetailsForm.get('occurences')?.value?.id;
    return [
      Occurrences.Daily,
      Occurrences.Weekly,
      Occurrences.Monthly,
    ].includes(id);
  },
  occurencesCount: (comp: ScheduleDetailsComponent) => {
    const id: number = comp.scheduleDetailsForm.get('occurences')?.value?.id;
    return [
      Occurrences.Daily,
      Occurrences.Weekly,
      Occurrences.Monthly,
      Occurrences.Yearly,
    ].includes(id);
  },
  daysText: (comp: ScheduleDetailsComponent) =>
    comp.scheduleDetailsForm.get('occurences').value?.id === Occurrences.Daily,
  weeksOnText: (comp: ScheduleDetailsComponent) =>
    comp.scheduleDetailsForm.get('occurences').value?.id === Occurrences.Weekly,
  onText: (comp: ScheduleDetailsComponent) =>
    comp.scheduleDetailsForm.get('occurences').value?.id ===
    Occurrences.EveryWeekday,
  monthText: (comp: ScheduleDetailsComponent) =>
    comp.scheduleDetailsForm.get('occurences').value?.id ===
    Occurrences.Monthly,
  yearText: (comp: ScheduleDetailsComponent) =>
    comp.scheduleDetailsForm.get('occurences').value?.id === Occurrences.Yearly,
  weekDayChip: (comp: ScheduleDetailsComponent) => {
    const id: number = comp.scheduleDetailsForm.get('occurences')?.value?.id;
    return [Occurrences.EveryWeekday, Occurrences.Weekly].includes(id);
  },
  monthOnGroup: (comp: ScheduleDetailsComponent) =>
    comp.scheduleDetailsForm.get('occurences').value?.id ===
    Occurrences.Monthly,
  yearlyOnGroup: (comp: ScheduleDetailsComponent) =>
    comp.scheduleDetailsForm.get('occurences').value?.id === Occurrences.Yearly,
  monthOnTheGroup: (comp: ScheduleDetailsComponent) =>
    comp.scheduleDetailsForm.get('occurences').value?.id ===
    Occurrences.Monthly,
  yearlyOnTheGroup: (comp: ScheduleDetailsComponent) =>
    comp.scheduleDetailsForm.get('occurences').value?.id === Occurrences.Yearly,
  repeatUntilSection: (comp: ScheduleDetailsComponent) =>
    comp.scheduleDetailsForm.get('occurences').value?.id !==
    Occurrences.DoNotRepeat &&
    (comp.isNew || !comp.isEditingSession || comp.isEdititingMeetingSeries),
  repeatUntilCount: (comp: ScheduleDetailsComponent) =>
    comp.scheduleDetailsForm.get('repeatUntil').value?.id ===
    RepeatUntil.EndAfterOccurrences,
  occurencesText: (comp: ScheduleDetailsComponent) =>
    comp.scheduleDetailsForm.get('repeatUntil').value?.id ===
    RepeatUntil.EndAfterOccurrences,
  endByDate: (comp: ScheduleDetailsComponent) =>
    comp.scheduleDetailsForm.get('repeatUntil').value?.id ===
    RepeatUntil.EndByDate,
};
